/**
 * Pure helpers / types for the shared Terraform plan-approval panel.
 */
import type { ReactNode } from 'react'
import { formatApiError } from '@/utils/format'
import type { TerraformPlanRecord } from '@/types'

/** A plan history record; outputs are product specific (never contain secrets). */
export type PlanRecord = TerraformPlanRecord

/** How plans/applies are executed: the GitHub workflow, or the development-only local executor. */
export interface PlanExecutor {
  mode: 'github' | 'local'
  /** False when approving a plan cannot create anything (local executor with its apply switch off). */
  apply_enabled: boolean
  /** Local, development-only: an administrator may approve their own plan. */
  self_approval_enabled?: boolean
  note: string
}

/** Product-specific wording of the decision panel (the default is the Terraform wording). */
export interface PlanApprovalCopy {
  /** Approve button, default "Approve plan". */
  approveButton?: string
  /** Confirm button after clicking approve, default "Yes, apply plan". */
  confirmApproveButton?: string
  /** Sentence describing what approving does; receives the plan counts. Default: terraform apply wording. */
  describeApprove?: (counts: string) => ReactNode
}

export interface PlanDetailLike<TConfig> {
  config: TConfig | null
  plans: PlanRecord[]
  can_approve: boolean
  executor?: PlanExecutor
}

/** True when the backend says an approved plan will NOT be applied (local executor, apply off). */
export function applyIsDisabled(executor: PlanExecutor | undefined): boolean {
  return executor?.mode === 'local' && !executor.apply_enabled
}

/** Shown to an administrator who is allowed to approve their own plan (local, development-only switch). */
export function selfApprovalHint(executor: PlanExecutor | undefined, canApprove: boolean): string | null {
  return executor?.self_approval_enabled && canApprove ? 'You can approve your own plan in this local environment.' : null
}

/** What a user who cannot decide sees while the plan awaits approval (null: nothing to wait for). */
export function waitingForApprovalText(executor: PlanExecutor | undefined, canApprove: boolean): string | null {
  if (executor?.self_approval_enabled) return canApprove ? null : 'Waiting for an administrator to approve this plan.'
  return canApprove ? null : 'Waiting for an administrator (other than the requester) to approve this plan.'
}

/** Approve / reject errors: show `detail` as-is, otherwise flatten the DRF body. */
export function planDecisionErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (data && typeof data === 'object' && 'detail' in data) return String((data as { detail: unknown }).detail)
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data.trim()
  if (data && typeof data === 'object') return formatApiError(err)
  return err instanceof Error ? err.message : 'Request failed'
}
